import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'
import { API_FILE_URL } from '../constant/api'

// ── Helpers ───────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || API_FILE_URL

const fmt      = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
const fmtTime  = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'
const money    = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'
const num      = (n) => n != null ? Number(n).toLocaleString() : '0'

const avatarBg = (str = '') => {
  const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}
const initials = (name = '') => (name.trim().charAt(0) || '?').toUpperCase()

const parseThumb = (thumbnail) => {
  if (!thumbnail) return null
  try {
    // Try to parse JSON array (product images are stored as JSON arrays)
    const arr = typeof thumbnail === 'string' && thumbnail.startsWith('[')
      ? JSON.parse(thumbnail)
      : null
    const raw = arr ? arr[0] : thumbnail
    if (!raw) return null
    const url = String(raw).trim()
    // GCS URLs are already complete public URLs — use directly
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    // Local upload path fallback
    const filename = url.replace(/\\/g, '/').split('/').pop()
    return `${API_BASE}/${filename}`
  } catch {
    return null
  }
}

const STATUS_MAP = {
  1: { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Active'   },
  0: { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Hidden'   },
}
const statusStyle = (s) => STATUS_MAP[s] ?? STATUS_MAP[Number(s)] ?? { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: '—' }

// ── Shared styles ─────────────────────────────────────────────────────────────
const SS = {
  select: { border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" },
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

function StatusBadge({ status }) {
  const s = statusStyle(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
    </span>
  )
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────
function DeleteModal({ reel, onClose, onConfirm, loading }) {
  if (!reel) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: '#fff', borderRadius: 14, zIndex: 401, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.97) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }`}</style>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Delete Clip?</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
            "<b>{reel.title || `Clip #${reel.reel_id}`}</b>" by <b>{reel.creator_name || reel.creator_phone}</b> will be permanently deleted. This cannot be undone.
          </div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Reel Detail Drawer ────────────────────────────────────────────────────────
function ReelDrawer({ reel, onClose, onDelete, onToggleStatus }) {
  const [tab,        setTab]        = useState('details')
  const [detail,     setDetail]     = useState(null)
  const [detailLoad, setDetailLoad] = useState(false)

  useEffect(() => {
    if (!reel) return
    setTab('details')
    setDetail(null)
    setDetailLoad(true)
    api.get(`/reels/admin/reels/${reel.reel_id}`)
      .then(res => setDetail(res.data.reel))
      .catch(() => setDetail(reel))
      .finally(() => setDetailLoad(false))
  }, [reel?.reel_id])

  if (!reel) return null
  const d       = detail || reel
  const thumb   = parseThumb(d.thumbnail_url || d.thumbnail || d.video_url)

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn { from { transform:translateX(40px);opacity:0 } to { transform:translateX(0);opacity:1 } }`}</style>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Thumbnail */}
          <div style={{ width: 48, height: 64, borderRadius: 8, background: '#111317', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
            {thumb
              ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>▶</div>
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title || `Clip #${d.reel_id}`}</div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
              by {d.creator_name || d.creator_phone || '—'} · #{d.reel_id}
            </div>
          </div>
          <StatusBadge status={d.status} />
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', marginLeft: 6 }}>×</div>
        </div>

        {/* Engagement strip */}
        <div style={{ display: 'flex', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', gap: 20 }}>
          {[
            ['👁', num(d.views),    'Views'],
            ['❤️', num(d.likes),    'Likes'],
            ['💬', num(d.comment_count), 'Comments'],
            ['🛍', num(d.product_count), 'Products'],
          ].map(([icon, val, lbl]) => (
            <div key={lbl} style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 16 }}>{icon}</div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.4px' }}>{val}</div>
              <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.06em' }}>{lbl}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '0 20px' }}>
          {[['details','Details'],['products','Products'],['comments','Comments'],['likes','Likes']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === id ? '#e8440a' : 'transparent'}`, marginBottom: -1 }}>{label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {detailLoad && <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading…</div>}

          {/* ── Details ── */}
          {!detailLoad && tab === 'details' && (
            <>
              {/* Video preview */}
              {(d.video_url || d.thumbnail) && (
                <div style={{ background: '#111317', borderRadius: 10, overflow: 'hidden', marginBottom: 16, aspectRatio: '9/16', maxHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {d.video_url
                    ? <video src={d.video_url} controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : thumb ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontSize: 32 }}>▶</span>
                  }
                </div>
              )}

              {d.caption && (
                <div style={{ background: '#f9f9f7', borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                  {d.caption}
                </div>
              )}

              {[
                ['Clip ID',    d.reel_id],
                ['Creator',    d.creator_name || '—'],
                ['Phone',      d.creator_phone || '—'],
                ['Shop',       d.shop_name || '—'],
                ['Title',      d.title || '—'],
                ['Status',     <StatusBadge status={d.status} />],
                ['Views',      num(d.views)],
                ['Likes',      num(d.likes)],
                ['Comments',   num(d.comment_count)],
                ['Products',   num(d.product_count)],
                ['Posted',     fmtTime(d.created_at)],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f3f3f0' }}>
                  <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: typeof v === 'string' ? "'JetBrains Mono',monospace" : 'inherit', color: '#111317', maxWidth: 260, textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </>
          )}

          {/* ── Products ── */}
          {!detailLoad && tab === 'products' && (
            !d.products?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No products tagged in this clip.</div>
              : d.products.map((p, i) => {
                  const imgUrl = parseThumb(p.images)
                  return (
                    <div key={p.prod_id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f3f3f0' }}>
                      <div style={{ width: 46, height: 46, borderRadius: 8, background: '#f3f3f0', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                        {imgUrl
                          ? <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display='none' }} />
                          : '📦'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{p.category || p.shop_name || '—'}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>{money(p.price)}</div>
                        <div style={{ fontSize: 10, color: p.quantity > 0 ? '#15803d' : '#b91c1c', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
                          {p.quantity > 0 ? `${p.quantity} in stock` : 'Out of stock'}
                        </div>
                      </div>
                    </div>
                  )
                })
          )}

          {/* ── Comments ── */}
          {!detailLoad && tab === 'comments' && (() => {
            const comments = d.comments || [];
            if (!comments.length) return (
              <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No comments yet.</div>
            );
            const deleteComment = (commentId) => {
              if (window.confirm('Delete this comment?')) {
                api.delete(`/reels/admin/reels/${d.reel_id}/comments/${commentId}`)
                  .then(() => setDetail(prev => ({ ...prev, comments: prev.comments.filter(x => x.comment_id !== commentId && x.parent_id !== commentId) })))
                  .catch(() => alert('Failed to delete comment'));
              }
            };
            const renderComment = (c, isReply = false) => (
              <div key={c.comment_id} style={{ padding: isReply ? '8px 0' : '12px 0', borderBottom: isReply ? 'none' : '1px solid #f3f3f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: isReply ? 22 : 28, height: isReply ? 22 : 28, borderRadius: 6, background: avatarBg(c.username || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isReply ? 9 : 11, fontWeight: 800, flexShrink: 0 }}>
                    {initials(c.username)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: isReply ? 11 : 12, fontWeight: 700 }}>{c.username || 'Anonymous'}</span>
                    {c.flagged && <span style={{ marginLeft: 6, fontSize: 9, background: '#fef2f2', color: '#b91c1c', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>flagged</span>}
                    <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginLeft: 8 }}>{fmtTime(c.created_at)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, paddingLeft: isReply ? 30 : 36 }}>
                  <div style={{ flex: 1, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{c.content}</div>
                  <button onClick={() => deleteComment(c.comment_id)}
                    style={{ padding: '3px 8px', borderRadius: 5, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    Del
                  </button>
                </div>
              </div>
            );
            const topLevel = comments.filter(c => !c.parent_id);
            const replies = comments.filter(c => !!c.parent_id);
            return topLevel.map(parent => (
              <div key={parent.comment_id}>
                {renderComment(parent, false)}
                {replies.filter(r => r.parent_id === parent.comment_id).map(reply => (
                  <div key={reply.comment_id} style={{ display: 'flex', paddingLeft: 28, paddingBottom: 4 }}>
                    <div style={{ width: 2, background: '#e5e7eb', borderRadius: 2, marginRight: 10, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>{renderComment(reply, true)}</div>
                  </div>
                ))}
              </div>
            ));
          })()}

          {/* ── Likes ── */}
          {!detailLoad && tab === 'likes' && (
            !d.likers?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No likes yet.</div>
              : d.likers.map((u, i) => (
                  <div key={u.user_id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f3f3f0' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 7, background: avatarBg(u.username || u.phone || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                      {initials(u.username || u.phone)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{u.username || 'No username'}</div>
                      <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{u.phone}</div>
                    </div>

                  </div>
                ))
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
          <button
            onClick={() => onToggleStatus(d)}
            style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: d.status === 1 ? '#fef2f2' : '#f0fdf4', color: d.status === 1 ? '#b91c1c' : '#15803d', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {d.status === 1 ? 'Hide Clip' : 'Restore Clip'}
          </button>
          <button
            onClick={() => { onDelete(d); onClose() }}
            style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #fecaca', cursor: 'pointer', background: '#fff5f5', color: '#b91c1c', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Delete Clip
          </button>
        </div>
      </div>
    </>
  )
}

// ── Add Clip Modal (admin posts a clip on behalf of a seller's shop) ──────────
function AddClipModal({ onClose, onAdded }) {
  const videoRef = useRef(null)
  const thumbRef = useRef(null)

  const [shop,         setShop]         = useState(null)
  const [shopSearch,   setShopSearch]   = useState('')
  const [shopResults,  setShopResults]  = useState([])
  const [shopLoading,  setShopLoading]  = useState(false)

  const [videoFile,    setVideoFile]    = useState(null)
  const [thumbFile,    setThumbFile]    = useState(null)
  const [thumbPreview, setThumbPreview] = useState(null)
  const [caption,      setCaption]      = useState('')
  const [progress,     setProgress]     = useState(0)
  const [stage,        setStage]        = useState('idle') // idle | uploading | saving | done | error
  const [errMsg,       setErrMsg]       = useState('')

  const [products,       setProducts]       = useState([])
  const [productsLoading,setProductsLoading]= useState(false)
  const [tagged,          setTagged]        = useState([])
  const [showPicker,      setShowPicker]    = useState(false)
  const [pickerSearch,    setPickerSearch]  = useState('')

  // Debounced shop search — active shops only
  useEffect(() => {
    if (!shopSearch.trim()) { setShopResults([]); return }
    setShopLoading(true)
    const t = setTimeout(() => {
      api.get(`/shops/admin/shops?${new URLSearchParams({ search: shopSearch.trim(), status: '1', limit: 8 })}`)
        .then(res => setShopResults(res.data?.shops ?? []))
        .catch(() => setShopResults([]))
        .finally(() => setShopLoading(false))
    }, 350)
    return () => clearTimeout(t)
  }, [shopSearch])

  // Once a shop is picked, load its products so they can be tagged to the clip
  useEffect(() => {
    setTagged([]); setProducts([])
    if (!shop) return
    setProductsLoading(true)
    api.get(`/products/shop/${shop.shop_id}`)
      .then(res => setProducts(res.data?.products ?? []))
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false))
  }, [shop])

  const handleVideo = (e) => {
    const f = e.target.files?.[0]
    if (f) setVideoFile(f)
    e.target.value = ''
  }
  const handleThumb = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setThumbFile(f)
    setThumbPreview(URL.createObjectURL(f))
    e.target.value = ''
  }

  const handleSubmit = async () => {
    if (!shop || !videoFile || !thumbFile) return
    setStage('uploading'); setProgress(0); setErrMsg('')
    try {
      const form = new FormData()
      form.append('video', videoFile)
      form.append('thumbnail', thumbFile)
      const uploadRes = await api.post('/reels/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => { if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 68)) },
        timeout: 5 * 60 * 1000,
      })
      setProgress(75); setStage('saving')
      const { videoUrl, thumbnailUrl } = uploadRes.data
      await api.post('/reels/admin/add', {
        shopId: shop.shop_id,
        caption: caption.trim() || null,
        videoUrl,
        thumbnailUrl,
        prodId: tagged.length > 0 ? tagged.map(p => p.prod_id) : null,
      })
      setProgress(100); setStage('done')
    } catch (e) {
      setErrMsg(e?.response?.data?.error ?? e?.message ?? 'Upload failed.')
      setStage('error')
    }
  }

  const busy = stage === 'uploading' || stage === 'saving'
  const fmtSize = (b) => b > 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`

  return (
    <>
      <div onClick={!busy ? onClose : undefined} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 500 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '100%', maxWidth: 440, maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 16, zIndex: 501, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid #e9e9e4', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>Add Clip for Seller</div>
          <button onClick={onClose} disabled={busy} style={{ background: 'none', border: 'none', fontSize: 22, color: '#9a9a94', cursor: busy ? 'not-allowed' : 'pointer', lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ padding: '20px 20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>

          {stage === 'done' ? (
            <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>🎉</div>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Clip Published!</div>
              <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: tagged.length > 0 ? 16 : 24 }}>Posted to <b>{shop.shop_name}</b>'s profile.</div>
              {tagged.length > 0 && (
                <div style={{ background: 'rgba(232,68,10,0.06)', border: '1px solid rgba(232,68,10,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 12, fontWeight: 600, color: '#e8440a' }}>
                  🛍 {tagged.length} product{tagged.length > 1 ? 's' : ''} tagged — viewers can tap to buy instantly.
                </div>
              )}
              <button onClick={() => onAdded()} style={{ width: '100%', height: 46, borderRadius: 12, border: 'none', background: '#e8440a', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Shop picker */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, fontFamily: "'JetBrains Mono',monospace" }}>Seller / Shop *</div>
                {shop ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1.5px solid #e8440a', borderRadius: 10, padding: '10px 12px', background: '#fff8f5' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: avatarBg(shop.shop_name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{initials(shop.shop_name)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shop.shop_name}</div>
                      <div style={{ fontSize: 11, color: '#9a9a94' }}>{shop.category || 'Shop'}</div>
                    </div>
                    {!busy && (
                      <button onClick={() => { setShop(null); setShopSearch('') }} style={{ height: 28, padding: '0 10px', borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Change</button>
                    )}
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <input value={shopSearch} onChange={e => setShopSearch(e.target.value)} placeholder="Search shop by name, phone, or owner…"
                      style={{ width: '100%', boxSizing: 'border-box', height: 40, borderRadius: 10, border: '1.5px solid #e9e9e4', padding: '0 14px', fontSize: 13, outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
                    {shopSearch.trim() && (
                      <div style={{ border: '1px solid #e9e9e4', borderRadius: 10, marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
                        {shopLoading ? (
                          <div style={{ padding: '14px', textAlign: 'center', fontSize: 12, color: '#9a9a94' }}>Searching…</div>
                        ) : shopResults.length === 0 ? (
                          <div style={{ padding: '14px', textAlign: 'center', fontSize: 12, color: '#9a9a94' }}>No matching shops</div>
                        ) : shopResults.map(s => (
                          <div key={s.shop_id} onClick={() => { setShop(s); setShopResults([]) }}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f3f0' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, background: avatarBg(s.shop_name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{initials(s.shop_name)}</div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.shop_name}</div>
                              <div style={{ fontSize: 10, color: '#9a9a94' }}>{s.owner_name || s.owner_phone || ''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Video */}
              <input ref={videoRef} type="file" accept="video/*" onChange={handleVideo} style={{ display: 'none' }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, fontFamily: "'JetBrains Mono',monospace" }}>Video *</div>
                {videoFile ? (
                  <div style={{ border: '2px solid #e8440a', borderRadius: 12, padding: '12px 14px', background: '#fff8f5', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{videoFile.name}</div>
                      <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2 }}>{fmtSize(videoFile.size)}</div>
                    </div>
                    <button onClick={() => !busy && setVideoFile(null)} disabled={busy} style={{ height: 28, padding: '0 10px', borderRadius: 7, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}>Remove</button>
                  </div>
                ) : (
                  <div onClick={() => videoRef.current?.click()} style={{ border: '2px dashed #e9e9e4', borderRadius: 12, padding: '28px 20px', textAlign: 'center', cursor: 'pointer', background: '#fafaf8' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🎥</div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Tap to choose a video</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2 }}>MP4 · MOV · WebM</div>
                  </div>
                )}
              </div>

              {/* Thumbnail */}
              <input ref={thumbRef} type="file" accept="image/*" onChange={handleThumb} style={{ display: 'none' }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, fontFamily: "'JetBrains Mono',monospace" }}>Thumbnail *</div>
                {thumbPreview ? (
                  <div style={{ border: '2px solid #e9e9e4', borderRadius: 12, padding: 10, background: '#fafaf8', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img src={thumbPreview} alt="thumb" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#9a9a94', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{thumbFile?.name}</div>
                    <button onClick={() => !busy && (setThumbFile(null), setThumbPreview(null))} disabled={busy} style={{ height: 28, padding: '0 10px', borderRadius: 7, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}>Remove</button>
                  </div>
                ) : (
                  <div onClick={() => thumbRef.current?.click()} style={{ border: '2px dashed #e9e9e4', borderRadius: 12, padding: '18px 20px', textAlign: 'center', cursor: 'pointer', background: '#fafaf8' }}>
                    <div style={{ fontSize: 22, marginBottom: 6 }}>🖼</div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>Add a cover image</div>
                    <div style={{ fontSize: 11, color: '#9a9a94' }}>Required for content review</div>
                  </div>
                )}
              </div>

              {/* Caption */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, fontFamily: "'JetBrains Mono',monospace" }}>Caption</div>
                <textarea value={caption} onChange={e => setCaption(e.target.value)} maxLength={300} rows={3} disabled={busy}
                  placeholder="Say something about this clip…"
                  style={{ width: '100%', boxSizing: 'border-box', borderRadius: 10, border: '1.5px solid #e9e9e4', padding: '10px 14px', fontSize: 13, resize: 'none', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
              </div>

              {/* Tag Products — the selected shop's own catalog */}
              {shop && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, fontFamily: "'JetBrains Mono',monospace" }}>
                    Tag Products <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional — let viewers buy instantly)</span>
                  </div>

                  {tagged.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                      {tagged.map(p => {
                        const thumb = parseThumb(p.images)
                        return (
                          <div key={p.prod_id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff8f5', border: '1.5px solid rgba(232,68,10,0.35)', borderRadius: 8, padding: '5px 8px 5px 6px' }}>
                            {thumb ? <img src={thumb} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover' }} /> : <span style={{ fontSize: 14 }}>📦</span>}
                            <span style={{ fontSize: 11, fontWeight: 700, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                            <button type="button" onClick={() => !busy && setTagged(prev => prev.filter(x => x.prod_id !== p.prod_id))} disabled={busy}
                              style={{ width: 16, height: 16, borderRadius: 4, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 900, cursor: busy ? 'not-allowed' : 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <button type="button" onClick={() => setShowPicker(v => !v)} disabled={busy}
                    style={{ width: '100%', height: 38, borderRadius: 9, border: '1.5px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', color: '#111317', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    {showPicker ? 'Close product list' : tagged.length > 0 ? 'Edit tagged products' : `Browse ${shop.shop_name}'s products`}
                  </button>

                  {showPicker && (
                    <div style={{ border: '1px solid #e9e9e4', borderRadius: 10, marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
                      <div style={{ padding: 8, borderBottom: '1px solid #f3f3f0', position: 'sticky', top: 0, background: '#fff' }}>
                        <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} placeholder="Search products…"
                          style={{ width: '100%', boxSizing: 'border-box', height: 32, borderRadius: 7, border: '1px solid #e9e9e4', padding: '0 10px', fontSize: 12, outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
                      </div>
                      {productsLoading ? (
                        <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#9a9a94' }}>Loading products…</div>
                      ) : products.filter(p => p.name.toLowerCase().includes(pickerSearch.toLowerCase())).length === 0 ? (
                        <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#9a9a94' }}>No products found.</div>
                      ) : products.filter(p => p.name.toLowerCase().includes(pickerSearch.toLowerCase())).map(p => {
                        const isTagged = tagged.some(x => x.prod_id === p.prod_id)
                        const thumb = parseThumb(p.images)
                        return (
                          <div key={p.prod_id} onClick={() => setTagged(prev => isTagged ? prev.filter(x => x.prod_id !== p.prod_id) : [...prev, p])}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f3f0', background: isTagged ? 'rgba(232,68,10,0.06)' : 'transparent' }}>
                            <div style={{ width: 32, height: 32, borderRadius: 7, background: '#f3f3f0', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>
                              {thumb ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} /> : '📦'}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                              <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{Number(p.price ?? 0).toLocaleString()} RWF</div>
                            </div>
                            <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${isTagged ? '#e8440a' : '#d1d5db'}`, background: isTagged ? '#e8440a' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontSize: 11, fontWeight: 900 }}>
                              {isTagged ? '✓' : ''}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {(stage === 'uploading' || stage === 'saving') && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}>
                    <span>{stage === 'saving' ? 'Saving clip…' : 'Uploading…'}</span>
                    <span style={{ fontWeight: 800, color: '#e8440a', fontFamily: "'JetBrains Mono',monospace" }}>{progress}%</span>
                  </div>
                  <div style={{ height: 6, background: '#f0f0ec', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progress}%`, background: '#e8440a', borderRadius: 6, transition: 'width 0.3s ease' }} />
                  </div>
                </div>
              )}

              {stage === 'error' && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#b91c1c' }}>{errMsg}</div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} disabled={busy} style={{ flex: 1, height: 44, borderRadius: 10, border: '1.5px solid #e9e9e4', background: 'transparent', fontSize: 13, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
                <button onClick={handleSubmit} disabled={!shop || !videoFile || !thumbFile || busy}
                  style={{ flex: 2, height: 44, borderRadius: 10, border: 'none', background: (!shop || !videoFile || !thumbFile || busy) ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 800, cursor: (!shop || !videoFile || !thumbFile || busy) ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {stage === 'uploading' ? 'Uploading…' : stage === 'saving' ? 'Saving…' : 'Publish Clip'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Reels() {
  const [reels,        setReels]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [search,       setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [stats,        setStats]        = useState({ total: 0, active: 0, hidden: 0, total_views: 0, total_likes: 0 })
  const [selected,     setSelected]     = useState(null)
  const [deleting,     setDeleting]     = useState(null)
  const [deleteLoad,   setDeleteLoad]   = useState(false)
  const [view,         setView]         = useState('grid')   // 'grid' | 'list'
  const [showAdd,      setShowAdd]      = useState(false)

  const PER_PAGE = 18

  const fetchReels = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(search        && { search }),
        ...(statusFilter  && { status: statusFilter }),
      })
      const res = await api.get(`/reels/admin/reels?${params}`)
      setReels(res.data.reels      || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
      setStats(res.data.stats      || { total: 0, active: 0, hidden: 0, total_views: 0, total_likes: 0 })
    } catch {
      setError('Failed to load reels')
      // Mock data
      const mock = Array.from({ length: 12 }, (_, i) => ({
        reel_id:       i + 1,
        title:         i % 3 === 0 ? null : `Reel title ${i + 1}`,
        caption:       `Check out this amazing product! #kudos #live`,
        creator_name:  `Creator ${i + 1}`,
        creator_phone: `078${String(i+1).padStart(7,'0')}`,
        shop_name:     i % 4 === 0 ? null : `Shop ${Math.ceil((i+1)/3)}`,
        status:        i % 5 === 0 ? 0 : 1,
        views:         Math.floor(Math.random() * 50000),
        likes:         Math.floor(Math.random() * 5000),
        comment_count: Math.floor(Math.random() * 200),
        product_count: Math.floor(Math.random() * 5),
        thumbnail:     null,
        created_at:    new Date(Date.now() - i * 86400000 * 2).toISOString(),
      }))
      setReels(mock)
      setTotalPages(3); setTotalCount(54)
      setStats({ total: 54, active: 48, hidden: 6, total_views: 1240000, total_likes: 89000 })
    } finally { setLoading(false) }
  }, [page, search, statusFilter])

  useEffect(() => { fetchReels() }, [fetchReels])
  useEffect(() => { setPage(1)  }, [search, statusFilter])

  const handleToggleStatus = async (reel) => {
    const newStatus = reel.status === 1 ? 0 : 1
    try {
      await api.patch(`/reels/admin/reels/${reel.reel_id}/status`, { status: newStatus })
      setReels(prev => prev.map(r => r.reel_id === reel.reel_id ? { ...r, status: newStatus } : r))
      if (selected?.reel_id === reel.reel_id) setSelected(prev => ({ ...prev, status: newStatus }))
    } catch { alert('Failed to update reel status') }
  }

  const handleDelete = async () => {
    if (!deleting) return
    setDeleteLoad(true)
    try {
      await api.delete(`/reels/admin/reels/${deleting.reel_id}`)
      setReels(prev => prev.filter(r => r.reel_id !== deleting.reel_id))
      setTotalCount(c => c - 1)
      setDeleting(null)
      setSelected(null)
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete reel') }
    finally { setDeleteLoad(false) }
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Clips Management</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>View, moderate and manage all platform clips.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => setShowAdd(true)}
            style={{ height: 38, padding: '0 16px', borderRadius: 8, border: 'none', background: '#e8440a', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            <span style={{ fontSize: 16 }}>+</span> Add Clip for Seller
          </button>
          {/* View toggle */}
          <div style={{ display: 'flex', background: '#fff', border: '1px solid #e9e9e4', borderRadius: 8, padding: 4, gap: 2 }}>
            {[['grid','⊞'],['list','☰']].map(([v, icon]) => (
              <button key={v} onClick={() => setView(v)} style={{ width: 34, height: 30, borderRadius: 6, border: 'none', background: view === v ? '#111317' : 'transparent', color: view === v ? '#fff' : '#9a9a94', fontSize: 14, cursor: 'pointer' }}>{icon}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Clips"   value={stats.total.toLocaleString()}                         />
        <StatCard label="Active"        value={stats.active.toLocaleString()}   accent="#15803d"     />
        <StatCard label="Hidden"        value={stats.hidden.toLocaleString()}   accent="#b91c1c"     />
        <StatCard label="Total Views"   value={num(stats.total_views)}          accent="#1d4ed8"     />
        <StatCard label="Total Likes"   value={num(stats.total_likes)}          accent="#e8440a"     />
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by title, caption or creator…" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={SS.select}>
          <option value="">All Status</option>
          <option value="1">Active</option>
          <option value="0">Hidden</option>
        </select>
        <button onClick={fetchReels} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{totalCount.toLocaleString()} clips</div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error} — showing mock data</div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading clips…</div>
      ) : reels.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>No clips found.</div>
      ) : view === 'grid' ? (

        /* ── GRID VIEW ── */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
          {reels.map(reel => {
            const thumb = parseThumb(reel.thumbnail_url || reel.thumbnail || reel.video_url)
            return (
              <div
                key={reel.reel_id}
                onClick={() => setSelected(reel)}
                style={{ background: '#111317', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', position: 'relative', aspectRatio: '9/14', transition: 'transform 0.15s', border: `2px solid ${reel.status === 0 ? 'rgba(239,68,68,0.5)' : 'transparent'}` }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                {/* Thumbnail */}
                {thumb
                  ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display='none'} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: 'rgba(255,255,255,0.3)' }}>▶</div>
                }

                {/* Overlay gradient */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 60%)', padding: '12px 10px 10px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
                    {reel.creator_name || reel.creator_phone || '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>👁 {num(reel.views)}</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>❤️ {num(reel.likes)}</span>
                  </div>
                </div>

                {/* Hidden badge */}
                {reel.status === 0 && (
                  <div style={{ position: 'absolute', top: 8, left: 8, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase' }}>Hidden</div>
                )}

                {/* Product count badge */}
                {reel.product_count > 0 && (
                  <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: 3 }}>🛍 {reel.product_count}</div>
                )}

                {/* Quick actions on hover */}
                <div className="reel-actions" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: 0, transition: 'opacity 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '0'}
                  onClick={e => e.stopPropagation()}
                >
                  <button onClick={() => setSelected(reel)} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#fff', color: '#111317', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>View</button>
                  <button onClick={() => setDeleting(reel)} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Delete</button>
                </div>
              </div>
            )
          })}
        </div>

      ) : (

        /* ── LIST VIEW ── */
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2.4fr 1.6fr 0.7fr 0.7fr 0.6fr 0.8fr 0.9fr 0.7fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
            {['Clip','Creator','Views','Likes','Comments','Products','Status','Actions'].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
            ))}
          </div>

          {reels.map((reel, i) => {
            const thumb = parseThumb(reel.thumbnail_url || reel.thumbnail || reel.video_url)
            return (
              <div
                key={reel.reel_id}
                onClick={() => setSelected(reel)}
                style={{ display: 'grid', gridTemplateColumns: '2.4fr 1.6fr 0.7fr 0.7fr 0.6fr 0.8fr 0.9fr 0.7fr', padding: '12px 20px', borderBottom: i < reels.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', transition: 'background 0.12s', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Reel */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 48, borderRadius: 6, background: '#111317', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {thumb ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} /> : <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>▶</span>}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reel.title || `Clip #${reel.reel_id}`}</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{fmt(reel.created_at)}</div>
                  </div>
                </div>
                {/* Creator */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{reel.creator_name || '—'}</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{reel.shop_name || 'No shop'}</div>
                </div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(reel.views)}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(reel.likes)}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(reel.comment_count)}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>
                  {reel.product_count > 0 ? <span>🛍 {reel.product_count}</span> : <span style={{ color: '#9a9a94' }}>—</span>}
                </div>
                <div><StatusBadge status={reel.status} /></div>
                <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setSelected(reel)} style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>View</button>
                  <button onClick={() => setDeleting(reel)} style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Del</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Drawer */}
      <ReelDrawer
        reel={selected}
        onClose={() => setSelected(null)}
        onDelete={r => setDeleting(r)}
        onToggleStatus={handleToggleStatus}
      />

      {/* Delete modal */}
      <DeleteModal
        reel={deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        loading={deleteLoad}
      />

      {/* Add clip modal */}
      {showAdd && (
        <AddClipModal
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); fetchReels() }}
        />
      )}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}